import _ from 'lodash'
import type { RefBeckyModel, RefTemplate } from '@wix/thunderbolt-becky-root'
import type {
	Data,
	DataMapName,
	Structure,
	Language,
	RefSchemas,
	PageSeoTranslation,
	MetricsReporter } from '@wix/thunderbolt-becky-types'
import {
	pointers
} from '@wix/thunderbolt-becky-types'
import { getByRefsis } from './byRefsis/byRefsis'
import {
	DATA_MAP_NAMES,
	INFLATED_MAP,
	INFLATED_MAP_TO_DATA_MAP_NAME,
	RESOLVED_REF_COMP,
	COMPONENTS_WITH_SLOTS_ASPECT,
	STRUCTURE_ASPECT,
	BUILDER_COMP_TO_NICKNAME_MAP_ASPECT,
	SITE_NAMESPACE,
	BUILDER_COMP_DATA_ASPECT,
} from './byRefsis/constants'
import { getViewModeSuffix } from './byRefsis/byRefUtils'
import { TEMPLATE_DATA_MAPS_TO_MERGE } from './constants'
import type { ByRefsisOps, RawModel } from './types'

type Translations = RefBeckyModel['translations']

const {
	data: { DATA_MAPS },
} = pointers

export const updateWidgetTemplate = (
	widgetTemplate: RefTemplate,
	templateKey: string,
	updateWidgetRootCompId: ByRefsisOps['updateWidgetRootCompId'],
	updateWidgetTranslation: ByRefsisOps['updateWidgetTranslation'],
	updateWidgetStructure: ByRefsisOps['updateWidgetStructure'],
	updateWidgetData: ByRefsisOps['updateWidgetData'],
	mergeTemplateData: (dataMapName: DataMapName, id: string, value: any) => void
) => {
	updateWidgetRootCompId(templateKey, widgetTemplate.rootId)

	for (const widgetTranslationsLanguage in widgetTemplate.translations) {
		const translatedDataItems =
			widgetTemplate.translations[widgetTranslationsLanguage as Language].data.document_data
		for (const documentDataItemId in translatedDataItems) {
			updateWidgetTranslation(
				templateKey,
				widgetTranslationsLanguage as Language,
				documentDataItemId,
				translatedDataItems[documentDataItemId]
			)
		}
	}

	for (const compId in widgetTemplate.structure) {
		updateWidgetStructure(templateKey, compId, widgetTemplate.structure[compId])
	}

	for (const dataMapName in widgetTemplate.data) {
		const dataMap = widgetTemplate.data[dataMapName as DataMapName]
		for (const id in dataMap) {
			updateWidgetData(templateKey, dataMapName as DataMapName, id, dataMap[id])

			if (TEMPLATE_DATA_MAPS_TO_MERGE.includes(dataMapName as DataMapName)) {
				mergeTemplateData(dataMapName as DataMapName, id, dataMap[id])
			}
		}
	}
}

export const resolveCommonAspects = (
	beckyModel: RefBeckyModel,
	schemas: RefSchemas,
	runAndReport: MetricsReporter['runAndReport'] = (fn) => fn()
) => {
	const {
		pageCompId,
		isResponsive,
		structure,
		masterPageStructure,
		data,
		masterPageData = {} as Data, // TODO fix becky testkit to always define this field in case of building by ref models
		translations,
		masterPageTranslations,
		experiments,
		viewMode,
		widgetTemplatesStore,
		resolveGhosts: keepGhostComponents,
		originalLanguage,
		language,
		compIdToNicknameMaps,
		builderCompIdToDataQuery,
	} = beckyModel

	const rawModel: RawModel = {}

	_.set(rawModel, [SITE_NAMESPACE, BUILDER_COMP_TO_NICKNAME_MAP_ASPECT], compIdToNicknameMaps)
	_.set(rawModel, [SITE_NAMESPACE, BUILDER_COMP_DATA_ASPECT], builderCompIdToDataQuery)

	const mergeMasterPageIntoPage = isResponsive && pageCompId !== 'masterPage'

	const store: { structure: Structure; data: Data } = {
		structure: { ...structure, ...(mergeMasterPageIntoPage && masterPageStructure) }, // naive implementation. byref may return orphan components.
		data: {} as Data,
	}

	const onInvalidation = (path: Array<string>, value: unknown) => {
		const [, aspect, id] = path

		if (aspect === STRUCTURE_ASPECT || aspect === RESOLVED_REF_COMP || aspect === COMPONENTS_WITH_SLOTS_ASPECT) {
			_.set(store, ['structure', id], value)
		} else if (DATA_MAP_NAMES.has(aspect as DataMapName) && value) {
			_.set(store, ['data', aspect, id], value)
		}
		// Unlike the other cases, each value in all inflatedMap aspects have various inflated dataItems (refArrays, variantRelations and their values).
		// Thats why we need to copy ALL properties of the value into the corresponding dataMap
		else if (aspect.endsWith(INFLATED_MAP)) {
			const dataMap = INFLATED_MAP_TO_DATA_MAP_NAME[aspect]
			_.set(store, ['data', dataMap], Object.assign(store.data[dataMap] || {}, value))
		} else {
			_.set(store, path, value)
		}
	}

	const widgetTemplatesMapsWithDataRefs: Partial<Data> = {}

	const byRefsis = runAndReport(
		() =>
			getByRefsis({
				onInvalidation,
				schemas,
				doNotInflateSharedParts: Boolean(experiments['specs.thunderbolt.doNotInflateSharedParts']),
				editorMode: false,
				rawModel,
			}),
		'getByRefsis'
	)

	runAndReport(() => {
		byRefsis.transaction(
			({
				updateStructure,
				updateData,
				updateTranslation,
				updateWidgetStructure,
				updateWidgetData,
				updateWidgetTranslation,
				updateWidgetRootCompId,
				updateEnvironment,
			}) => {
				updateEnvironment({
					experiments,
					environment: { viewMode, keepGhostComponents, originalLanguage, language },
				})
				for (const compId in structure) {
					const comp = structure[compId]
					updateStructure(compId, comp)
				}

				// TODO don't run byref on master page in responsive (even though we getResponsiveCleanMasterPageStructure()ing right before resolveCommonAspects())
				if (mergeMasterPageIntoPage) {
					for (const compId in masterPageStructure) {
						updateStructure(compId, masterPageStructure[compId])
					}
				}

				for (const dataMapName in data) {
					for (const id in data[dataMapName as DataMapName]) {
						const dataItem = data[dataMapName as DataMapName][id]
						updateData(dataMapName as DataMapName, id, dataItem)
					}
				}

				if (mergeMasterPageIntoPage) {
					for (const dataMapName in masterPageData) {
						for (const id in masterPageData[dataMapName as DataMapName]) {
							const dataItem = masterPageData[dataMapName as DataMapName][id]
							updateData(dataMapName as DataMapName, id, dataItem)
						}
					}
				}

				// master page translations are merged into page translations in processedPages.ts
				for (const translationLanguage in translations) {
					const translatedDataItems = translations[translationLanguage as Language].data.document_data
					for (const documentDataItemId in translatedDataItems) {
						updateTranslation(
							translationLanguage as Language,
							documentDataItemId,
							translatedDataItems[documentDataItemId]
						)
					}
				}

				for (const templateKey in widgetTemplatesStore[viewMode]) {
					updateWidgetTemplate(
						widgetTemplatesStore[viewMode][templateKey],
						`${templateKey}${getViewModeSuffix(viewMode)}`,
						updateWidgetRootCompId,
						updateWidgetTranslation,
						updateWidgetStructure,
						updateWidgetData,
						(dataMapName, id, value) => {
							_.set(widgetTemplatesMapsWithDataRefs, [dataMapName, id], value)
						}
					)
				}
			}
		)
	}, 'byRefsis.transaction')

	const mergedData = runAndReport(() => {
		return _.reduce(
			_.values(DATA_MAPS),
			(result, dataMapName) => {
				const mergeMasterPageData = TEMPLATE_DATA_MAPS_TO_MERGE.includes(dataMapName) || mergeMasterPageIntoPage
				// @ts-ignore
				result[dataMapName] = _.assign(
					widgetTemplatesMapsWithDataRefs[dataMapName],
					data[dataMapName],
					mergeMasterPageData ? masterPageData[dataMapName] : {},
					store.data[dataMapName]
				)

				return result
			},
			{} as Data
		)
	}, 'mergedData')

	const document_data = pointers.data.DATA_MAPS.DATA
	const seoTranslations = _({
		default: {
			data: { [document_data]: data.document_data },
		},
	})
		.assign(masterPageTranslations)
		.mapValues(
			(languageData) =>
				_(languageData.data[document_data])
					.pickBy({ type: 'Page' })
					.mapValues((pageData) => _.pick(pageData, ['advancedSeoData', 'pageUriSEO', 'parentPageId']))
					.value() as PageSeoTranslation
		)
		.value()

	return {
		structure: store.structure,
		data: mergedData,
		translations: {} as Translations,
		seoTranslations,
	}
}
